import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMount } from 'react-use';
import { DocumentCategory, ImageSide, useCreatePerson, useInvalidateGetKyc } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';

import { excludeTimeFromDate } from '../../../../resources/helpers';
import { useUploadKycDocument } from '../../../../resources/hooks/useUploadKycDocument';
import { addPersonPopupOpenState, addPersonPopupStepsState } from '../AddPersonPopup/AddPersonPopup.states';
import { addPersonWizardSteps, addPersonWizardStepSchemaMap, adoptedRoleToRoles } from './AddPersonWizard.constants';
import { extractValuesByStep } from './AddPersonWizard.helpers';
import { addPersonWizardStepState } from './AddPersonWizard.states';
import { FormValues, InvitationStep } from './AddPersonWizard.types';

export const useWizardStepIndex = () => {
    const activeStep = useAtomValue(addPersonWizardStepState);

    return useMemo(() => addPersonWizardSteps.findIndex((step) => step === activeStep), [activeStep]);
};

export const useWizardRouting = () => {
    const [activeStep, setActiveStep] = useAtom(addPersonWizardStepState);
    const [steps, setSteps] = useAtom(addPersonPopupStepsState);
    const stepIndex = useWizardStepIndex();

    useMount(() => {
        setActiveStep('role');
        setSteps(addPersonWizardSteps);
    });

    const moveStep = useCallback(
        (nextStepIndex: number) => {
            if (nextStepIndex >= 0 && steps.length - 1 >= nextStepIndex) {
                setActiveStep(steps[nextStepIndex]);
            }
        },
        [setActiveStep, steps]
    );

    const moveForward = useCallback(() => {
        moveStep(stepIndex + 1);
    }, [moveStep, stepIndex]);

    const moveBack = useCallback(() => {
        moveStep(stepIndex - 1);
    }, [moveStep, stepIndex]);

    const stepsCount = steps?.length ?? 0;

    return {
        step: activeStep,
        isFirstStep: stepIndex === 0,
        isLastStep: stepIndex === stepsCount - 1,
        hasOnlyOneStep: stepsCount === 1,
        moveForward,
        moveBack,
    };
};

export const useFormSubmit = ({ mutate }: Pick<ReturnType<typeof useCreatePerson>, 'mutate'>) => {
    const { onUploadDocuments } = useUploadKycDocument();
    const { companyId } = useCompanyIdBasedOnUrl();

    const handleSubmit = async ({ person }: FormValues, step?: InvitationStep) => {
        const personFields = step ? extractValuesByStep(person, step) : person;
        const { adoptedRoles, personDocuments, isUs, isWalletOwner, ...values } = personFields;
        const roles = adoptedRoleToRoles[adoptedRoles];

        const documentIds = personDocuments
            ? await onUploadDocuments(
                  personDocuments.documentType,
                  DocumentCategory.proof_of_identity,
                  personDocuments.requireBack
                      ? [
                            {
                                images: [
                                    { ...personDocuments.front?.[0], imageSide: ImageSide.front },
                                    { ...personDocuments.back?.[0], imageSide: ImageSide.back },
                                ],
                            },
                        ]
                      : [{ images: [{ ...personDocuments.front?.[0], imageSide: ImageSide.front }] }]
              )
            : [];

        const homeCountry = values.homeCountry || values.address?.country;

        return mutate({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: {
                person: {
                    ...values,
                    homeCountry,
                    isWalletOwner: isWalletOwner ?? false,
                    dob: values.dob ? excludeTimeFromDate(values.dob) : undefined,
                    roles,
                    documentIds,
                },
            },
        });
    };

    return { onSubmit: handleSubmit };
};

export const useWizardForm = (moveForward: VoidFunction, isLastStep: boolean) => {
    const { companyId } = useCompanyIdBasedOnUrl();
    const activeStep = useAtomValue(addPersonWizardStepState);
    const setOpenAddPersonPopup = useSetAtom(addPersonPopupOpenState);
    const setSteps = useSetAtom(addPersonPopupStepsState);
    const invalidateGetKyc = useInvalidateGetKyc();

    const { mutate, isLoading } = useCreatePerson({
        onSuccess: () => {
            void invalidateGetKyc({ path: { organizationId: companyId } });
            setOpenAddPersonPopup(false);
        },
    });
    const { onSubmit } = useFormSubmit({ mutate });

    const form = useForm<FormValues>({
        defaultValues: { person: { verifyNeeded: false, isWalletOwner: false, isUs: false } },
        resolver: zodResolver(addPersonWizardStepSchemaMap[activeStep]),
    });

    const verifyNeeded = form.watch('person.verifyNeeded');

    useEffect(() => {
        setSteps(verifyNeeded ? addPersonWizardSteps : [addPersonWizardSteps[0]]);
    }, [setSteps, verifyNeeded]);

    const handleSubmit = form.handleSubmit(async ({ person }) => {
        if (!isLastStep) {
            moveForward();
        }

        if (!verifyNeeded || isLastStep) {
            return onSubmit({ person });
        }
    });

    return {
        form,
        onSubmit: handleSubmit,
        isLoading,
    };
};
